import { useCallback, useEffect, useRef, useState } from "react";
import miroService from "api/miro/miro.service";
import { IMiroStatusResponse } from "api/miro/miro.type";
import { getMiroErrorCode, isPaywallError } from "utils/miro";

export type MiroConnectionState = "idle" | "loading" | "ready" | "error";

const DISCONNECTED: IMiroStatusResponse = { connected: false };

/**
 * The caller's Miro connection (REF-6702). The server owns it — OAuth tokens never reach the
 * browser — so this only mirrors `GET integrations/miro/status` and exposes the three actions.
 *
 * Every async continuation checks it still owns the latest request (`requestRef`) before writing
 * state, so a slow status read can neither overwrite a newer one nor land after unmount
 * (`[SELECTION_ANCHOR_BY_IDENTITY]`). A `_handled` response is never read as a success.
 */
const useMiroConnection = (enabled: boolean) => {
  const [state, setState] = useState<MiroConnectionState>("idle");
  const [status, setStatus] = useState<IMiroStatusResponse>(DISCONNECTED);
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  // Ownership of the in-flight authorize attempt: ended by unmount or by the gate closing.
  const connectRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      connectRef.current += 1;
    };
  }, []);

  const refresh = useCallback(async (): Promise<IMiroStatusResponse | null> => {
    const requestId = ++requestRef.current;
    setState("loading");

    try {
      const res = await miroService.status();
      if (!mountedRef.current || requestId !== requestRef.current) return null;

      if (res?._handled || !res?.responseData) {
        setState("error");

        return null;
      }

      setStatus(res.responseData);
      setState("ready");

      return res.responseData;
    } catch {
      if (!mountedRef.current || requestId !== requestRef.current) return null;
      setState("error");

      return null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      // Invalidate any in-flight read so a revoked permission cannot repopulate the UI.
      requestRef.current += 1;
      connectRef.current += 1;
      setState("idle");
      setStatus(DISCONNECTED);

      return;
    }

    void refresh();
  }, [enabled, refresh]);

  /**
   * Full-page redirect to Miro's consent screen. `failed` = the BE refused or answered with a url that is
   * not https (never navigate to an attacker-shaped scheme such as `javascript:`); `paywall` = a 402 whose
   * UI `HttpClient` already showed (reset state, say nothing more); `stale` = the attempt was abandoned
   * (unmount / gate closed) and nothing must happen.
   */
  const connect = useCallback(
    async (returnTo: string): Promise<"redirected" | "failed" | "paywall" | "stale"> => {
      const attempt = ++connectRef.current;
      const owns = () => mountedRef.current && attempt === connectRef.current;

      try {
        const res = await miroService.authorize({ return_to: returnTo });
        if (!owns()) return "stale";
        if (res?._handled) return "failed";

        const url = res?.responseData?.authorize_url;
        if (typeof url !== "string" || !url.startsWith("https://")) return "failed";

        window.location.href = url;

        return "redirected";
      } catch (err) {
        if (!owns()) return "stale";
        if (isPaywallError(err)) return "paywall";
        // `miro.not_configured` means the integration is off for this environment: reflect it.
        if (getMiroErrorCode(err)) void refresh();

        return "failed";
      }
    },
    [refresh]
  );

  const disconnect = useCallback(async (): Promise<boolean> => {
    const requestId = ++requestRef.current;

    try {
      const res = await miroService.disconnect();
      if (res?._handled) return false;
      if (mountedRef.current && requestId === requestRef.current) {
        setStatus({ ...status, connected: false, needs_reauth: false, miro_user: null, miro_team: null, connected_at: null });
        setState("ready");
      }

      return true;
    } catch {
      return false;
    }
  }, [status]);

  const isAvailable = status.available !== false;

  return {
    state,
    status,
    isLoading: state === "loading" || state === "idle",
    isAvailable,
    isConnected: status.connected === true && status.needs_reauth !== true,
    needsReauth: status.needs_reauth === true,
    refresh,
    connect,
    disconnect,
  };
};

export default useMiroConnection;
